<template>
  <div class="container-fluid">
    <div class="input-group pb-3">
      <RouterLink :to="{ name: 'labels-item' }" class="btn btn-outline-secondary">
        <font-awesome-icon :icon="['fas', 'chevron-left']" />
        {{ $t('action.back') }}
      </RouterLink>
      <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
        <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
        <font-awesome-icon v-else :icon="['fas', 'rotate']" />
        <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
      </button>
      <span class="input-group-text ms-2">
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
      </span>
      <input type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')" :disabled="isLoading" />
      <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="itemFilter || filterWord || selectedRestockPullType !== null">
        <font-awesome-icon :icon="['fas', 'filter-slash']" />
        {{ runtimeTranscription ?? filterWord }}
      </button>
      <button v-for="pullType in RESTOCK_PULL_TYPE" :key="pullType"
        type="button"
        class="btn btn-outline-secondary"
        :class="{ active: selectedRestockPullType === pullType }"
        @click="toggleRestockPullTypeFilter(pullType)"
        :disabled="isLoading"
        :title="$t(`enum.restockPullType.${pullType}`)">
        <RestockPullTypeIcon :restock-pull-type="pullType" />
      </button>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    <div v-else-if="filteredList" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
      <table v-if="filteredList.length > 0" class="table table-hover table-sm table-fit align-middle">
        <thead>
          <tr>
            <th>
              <input type="checkbox" class="form-check-input"
                :checked="allFilteredSelected"
                :indeterminate.prop="someFilteredSelected"
                :disabled="filteredList.length === 0"
                @click.stop="toggleSelectAll" />
            </th>
            <th v-if="selectedSiteId === 0">{{ $t('global.site') }}</th>
            <th>
              {{ $t('field.generic.item') }}
              <font-awesome-icon :icon="['fas', 'sort-up']" />
            </th>
            <th class="d-table-cell d-sm-none">{{ $t('global.propertiesShort') }}</th>
            <th class="d-none d-lg-table-cell" :title="$t('page.item.form.restockPullType')">{{ $t('field.item.restockPullType') }}</th>
            <th v-if="userSettingsStore.data.showBarcode"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="stock in filteredList" :key="stock.id" @click="handleRowClick(stock, $event)" @mousedown="handleRowMouseDown($event)" :class="{ 'is-selected': stock.isStored }">
            <td><input type="checkbox" class="form-check-input" :checked="stock.isStored"></td>
            <td v-if="selectedSiteId === 0"><sup class="text-secondary">{{ stock.site.code }}&nbsp;</sup>{{ stock.site.name }}</td>
            <td><ItemField :item="stock.item" /></td>
            <td class="d-none d-lg-table-cell">
              <RestockPullTypeIcon :restock-pull-type="getSiteRestockPullType(stock.item)" class="text-secondary" />
            </td>
            <td v-if="userSettingsStore.data.showBarcode" class="bg-white p-1">
              <TradeItemBarcode :itemId="stock.item.id" :itemSignature="stock" :size="'3rem'" />
            </td>
          </tr>
        </tbody>
      </table>
      <span v-else>{{ $t('error.noData') }}</span>
    </div>
    <div v-else class="loading">
      {{ $t('status.loadingEllipsis') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { storeToRefs } from 'pinia';
import { useSessionStore, useUserSettingsStore, useItemLabelsStore, useItemsStore, useItemSiteSettingsStore } from '@/stores';
import TradeItemBarcode from '@/components/diagram/TradeItemBarcode.vue';
import RestockPullTypeIcon from "@/components/masterdata/RestockPullTypeIcon.vue";
import { ItemField } from "@/components/fields";
import FrontendStock from "@/models/frontend/Stock";
import IItemWithSignatureRaw from "@/models/IItemWithSignatureRaw";
import Item from "@/models/frontend/Item";
import { RESTOCK_PULL_TYPE, RestockPullType } from "@/models/Enums";

type SelectableStock = FrontendStock &
{
  isStored: boolean;
}

const userSettingsStore = useUserSettingsStore();
const itemLabelsStore = useItemLabelsStore();
const itemSiteSettingsStore = useItemSiteSettingsStore();

const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);

const itemsStore = useItemsStore();
const { data, error } = storeToRefs(itemsStore);
const isLoading = ref(false);
const sort = ref(false);
const baseSortedList = ref<SelectableStock[]>([]);
const itemFilter = ref("");
const selectedRestockPullType = ref<RestockPullType | null>(null);
const offsetTop = ref(0);

const runtimeTranscription = ref(null as string | null);
const filterWord = ref(null as string | null);
const speechVoices = ref([] as SpeechSynthesisVoice[]);

onMounted(async () => {
  await refresh();
  // this is a workaround for the speechSynthesis.getVoices() not being available immediately
  if ('speechSynthesis' in window) {
    speechVoices.value = window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = function() {
      speechVoices.value = window.speechSynthesis.getVoices();
    };
  }
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

async function refresh() {
  error.value = "";
  isLoading.value = true;

  try {
    await itemsStore.fetchAll();
  } finally {
    isLoading.value = false;
  }
}

const resetFilter = () => {
  filterWord.value = null;
  itemFilter.value = "";
  selectedRestockPullType.value = null;
}

const toggleRestockPullTypeFilter = (pullType: RestockPullType) => {
  selectedRestockPullType.value = selectedRestockPullType.value === pullType ? null : pullType;
}

const isStored = (stock: IItemWithSignatureRaw): boolean =>{
  return itemLabelsStore.isStored(stock);
}

const getSiteRestockPullType = (item: Item) => {
  return itemSiteSettingsStore.getByItemId(item.id)?.restockPullType ?? item.restockPullType;
}

const lastClickedItemId = ref<number | null>(null);

const toggleSignatureItemSelection = (item: SelectableStock) => {
  if (itemLabelsStore.isStored(item)) {
    itemLabelsStore.removeSignatureItem(item);
  } else {
    itemLabelsStore.addSignatureItem(item);
  }
};

const setSignatureItemSelection = (item: SelectableStock, selected: boolean) => {
  const isCurrentlyStored = itemLabelsStore.isStored(item);
  if (selected && !isCurrentlyStored) {
    itemLabelsStore.addSignatureItem(item);
  } else if (!selected && isCurrentlyStored) {
    itemLabelsStore.removeSignatureItem(item);
  }
};

const handleRowMouseDown = (event: MouseEvent) => {
  // Prevent the browser's default text selection when extending a selection range.
  if (event.shiftKey) {
    event.preventDefault();
  }
};

const handleRowClick = (stock: SelectableStock, event: MouseEvent) => {
  const list = filteredList.value;

  if (event.shiftKey && lastClickedItemId.value !== null) {
    const anchorIdx = list.findIndex((s) => s.item.id === lastClickedItemId.value);
    const targetIdx = list.findIndex((s) => s.item.id === stock.item.id);

    if (anchorIdx !== -1 && targetIdx !== -1) {
      const [start, end] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
      const newState = !stock.isStored;
      for (let i = start; i <= end; i++) {
        const rangeStock = list[i];
        if (rangeStock) {
          setSignatureItemSelection(rangeStock, newState);
        }
      }
      lastClickedItemId.value = stock.item.id;
      return;
    }
  }

  toggleSignatureItemSelection(stock);
  lastClickedItemId.value = stock.item.id;
};

const allFilteredSelected = computed(() =>
  filteredList.value.length > 0 && filteredList.value.every((s) => s.isStored)
);

const someFilteredSelected = computed(() =>
  filteredList.value.some((s) => s.isStored) && !allFilteredSelected.value
);

const toggleSelectAll = () => {
  if (allFilteredSelected.value) {
    for (const stock of [...filteredList.value]) {
      itemLabelsStore.removeSignatureItem(stock);
    }
  } else {
    for (const stock of filteredList.value) {
      if (!itemLabelsStore.isStored(stock)) {
        itemLabelsStore.addSignatureItem(stock);
      }
    }
  }
};

const selectableStocks = computed(() => {
  return data.value.map((item) => {
    var selectableStock = { item: item } as SelectableStock;
    selectableStock.isStored = isStored(selectableStock);
    return selectableStock;
  });
});

const sortedList = computed(() => {
  if (sort.value) {
    return baseSortedList.value
  }
  else {
    return [...selectableStocks.value].sort((a, b) =>
      a.item.name.localeCompare(b.item.name)
    );
  }
});

const filteredList = computed(() => {
  let words = [] as string[];
  if (filterWord.value !== null) {
    words = filterWord?.value.toLocaleLowerCase().replace(/[^\wäöü\s]/g, '').split(' ');
  }

  return sortedList.value.filter((item) => {
    return (
      (item.item.name.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1 || item.item.code.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1) &&
      (words.length === 0 || words.some((element) => item.item.name.toLocaleLowerCase().split(' ').includes(element))) &&
      (selectedRestockPullType.value === null || selectedRestockPullType.value === getSiteRestockPullType(item.item))
    );
  }) as SelectableStock[];
});
</script>
